import { useLoader, type LoaderProto } from '@react-three/fiber';
import { type FC, memo, useContext, useEffect } from 'react';
import type {
  SphericalImageSetFromUrlExtendedProps,
  SphericalImageProps,
  SphericalImageSetJson,
} from '../../types';
import { FileLoaderOptionsContext } from '../FileLoaderOptionsContext';
import { PropsLoader } from '../PropsLoader';
import { InstancedSphericalImageSet } from './InstancedSphericalImageSet';

const joinUrl = (baseUrl: string, destination: string) => {
  if (destination.startsWith('./')) {
    const index = baseUrl.lastIndexOf('/');
    return baseUrl.slice(0, index) + destination.slice(1);
  }
  return destination;
};

class SphericalImageSetLoader extends PropsLoader<SphericalImageSetJson> {}

export const SphericalImageSetFromUrl: FC<SphericalImageSetFromUrlExtendedProps> = memo((props) => {
  const { url, requestHeader } = props;
  const fileLoaderOptions = useContext(FileLoaderOptionsContext);
  const loadedImages = useLoader<SphericalImageSetJson, string, LoaderProto<SphericalImageSetJson>>(
    SphericalImageSetLoader,
    url,
    (loader) => {
      loader.setWithCredentials(fileLoaderOptions.withCredentials);
      if (requestHeader) {
        loader.setRequestHeader(requestHeader);
      }
    }
  );

  useEffect(() => {
    return () => {
      useLoader.clear(SphericalImageSetLoader, url);
    };
  }, [url]);

  const { sphericalImages }: { sphericalImages: SphericalImageProps[] } = loadedImages;

  const updatedImages = sphericalImages.map((imageProps) => {
    return {
      ...imageProps,
      url: joinUrl(url, imageProps.url),
      requestHeader,
    };
  });

  return <InstancedSphericalImageSet {...props} sphericalImages={updatedImages} />;
});
SphericalImageSetFromUrl.displayName = 'SphericalImageSetFromUrl';
